import React, { createContext, useContext, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { translations } from '../translations';
import { services, navigation } from '../data';

const LanguageContext = createContext();

// Helper to map slugs between languages
export const slugMap = {
  da: {
    'offshore-vind': 'offshore-wind',
    'logistik': 'logistics',
    'marine-operationer': 'marine-operations'
  },
  en: {
    'offshore-wind': 'offshore-vind',
    'logistics': 'logistik',
    'marine-operations': 'marine-operationer'
  }
};

export function translateUrl(pathname, targetLang) {
  if (targetLang === 'en') {
    if (pathname === '/' || pathname === '') return '/en';
    if (pathname === '/kontakt') return '/en/contact';
    if (pathname === '/ydelser' || pathname === '/services') return '/en/services';
    
    // Handle service detail pages
    const matchDa = pathname.match(/^\/(ydelser|services)\/([^/]+)/);
    if (matchDa) {
      const daSlug = matchDa[2];
      const enSlug = slugMap.da[daSlug] || daSlug;
      return `/en/services/${enSlug}`;
    }
    
    // Fallback
    return '/en';
  } else {
    // Target is 'da'
    if (pathname === '/en' || pathname === '/en/') return '/';
    if (pathname === '/en/contact') return '/kontakt';
    if (pathname === '/en/services') return '/ydelser';
    
    // Handle service detail pages
    const matchEn = pathname.match(/^\/en\/services\/([^/]+)/);
    if (matchEn) {
      const enSlug = matchEn[1];
      const daSlug = slugMap.en[enSlug] || enSlug;
      return `/ydelser/${daSlug}`;
    }
    
    // Fallback
    return '/';
  }
}

export function LanguageProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();

  // Determine language based on current URL path
  const isEnglish = location.pathname.startsWith('/en') && (location.pathname === '/en' || location.pathname.startsWith('/en/'));
  const lang = isEnglish ? 'en' : 'da';

  // Set html lang attribute dynamically for SEO
  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  // Translate function with dot notation support (e.g. 'seo.home.title')
  const t = (key) => {
    const keys = key.split('.');
    let value = translations[lang];
    for (const k of keys) {
      if (value && value[k] !== undefined) {
        value = value[k];
      } else {
        return key; // Fallback to key
      }
    }
    return value;
  };

  const toggleLanguage = (targetLang) => {
    if (targetLang === lang) return;
    const newPath = translateUrl(location.pathname, targetLang);
    navigate(newPath);
  };

  const currentServices = services[lang];
  const currentNavigation = navigation[lang];

  return (
    <LanguageContext.Provider value={{
      lang,
      t,
      toggleLanguage,
      translateUrl: (path) => translateUrl(path, lang === 'da' ? 'en' : 'da'),
      services: currentServices,
      navigation: currentNavigation,
      allServices: services
    }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
